import { Stack, NavLink, Avatar } from '@mantine/core';
import { type ReactNode } from 'react';
import { FaSignOutAlt, FaChartBar, FaFolder, FaUsers, FaCog } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { Permission } from 'epd-validator-shared';

import { useAuth } from '../../providers/AuthProvider';
import { UserRole } from '../../types';

import classes from './Sidebar.module.css';

// Helper function to get user initials from name or email
function getUserInitials(user: { name?: string; email: string }) {
  if (user.name) {
    const nameParts = user.name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return user.name[0].toUpperCase();
  }
  return user.email[0].toUpperCase();
}

const ROLE_TO_DEFAULT_PATH: Partial<Record<UserRole, string>> = {
  [UserRole.PRACTITIONER]: '/projects',
  [UserRole.ADMIN]: '/admin/users',
  [UserRole.VERIFIER]: '/verifier/projects',
};

interface NavItemProps {
  to?: string;
  label: string;
  icon: ReactNode;
  onClick: (e: React.MouseEvent) => void;
  requiredPermission?: Permission;
}

function NavItem({ to, label, icon, onClick, requiredPermission }: NavItemProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (requiredPermission && !user?.permissions?.includes(requiredPermission)) {
    return null;
  }

  let currentPathForMatching = location.pathname;
  if (location.pathname === '/' && user?.role) {
    const defaultPath = ROLE_TO_DEFAULT_PATH[user.role];
    if (defaultPath) {
      currentPathForMatching = defaultPath;
    }
  }
  const isActive = to ? currentPathForMatching.startsWith(to) : false;

  const handleItemClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onClick) {
      onClick(e); // Execute the passed onClick (e.g., onNavItemClick or handleLogout)
    }
    if (to) {
      navigate(to); // Navigate if 'to' is provided
    }
  };

  return (
    <NavLink
      href={to || '#'}
      label={label}
      leftSection={icon}
      active={isActive}
      onClick={handleItemClick}
      classNames={{
        root: classes.link,
      }}
      rightSection={isActive && <span className={classes.activeIndicator} />}
    />
  );
}

interface ProfileNavItemProps {
  to: string;
  user: { name?: string; email: string };
  isActive?: boolean;
  onClick: (e: React.MouseEvent) => void;
}

// Special NavItem for profile with avatar and user name
function ProfileNavItem({ to, user, isActive, onClick }: ProfileNavItemProps) {
  const initials = getUserInitials(user);
  const displayName = user.name || user.email;
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default anchor navigation
    onClick(e);
    navigate(to);
  };

  return (
    <NavLink
      href={to}
      active={isActive}
      onClick={handleClick}
      label={<span className={classes.profileName}>{displayName}</span>}
      leftSection={<Avatar radius="xl">{initials}</Avatar>}
      classNames={{
        root: classes.link,
      }}
      rightSection={isActive && <span className={classes.activeIndicator} />}
    />
  );
}

interface SidebarProps {
  onNavItemClick: () => void;
}

export function Sidebar({ onNavItemClick }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
    onNavItemClick();
  };

  // Default user object for type safety
  const userWithFallback = user || { email: 'User' };

  return (
    <Stack className={classes.navbar} h="100%" justify="space-between" pt="md">
      <Stack flex={1} gap="xs">
        <NavItem
          to="/projects"
          label="Projects"
          icon={<FaFolder size={20} />}
          onClick={onNavItemClick}
          requiredPermission={Permission.VIEW_PROJECTS_LIST}
        />
        <NavItem
          to="/verifier/pool"
          label="Verifier Pool"
          icon={<FaChartBar size={20} />}
          onClick={onNavItemClick}
          requiredPermission={Permission.ACCESS_VERIFIER_POOL}
        />
        <NavItem
          to="/verifier/projects"
          label="Assigned Projects"
          icon={<FaFolder size={20} />}
          onClick={onNavItemClick}
          requiredPermission={Permission.ACCESS_VERIFIER_POOL}
        />
        <NavItem
          to="/admin/users"
          label="User Admin"
          icon={<FaUsers size={20} />}
          onClick={onNavItemClick}
          requiredPermission={Permission.MANAGE_USERS}
        />
        <NavItem
          to="/admin/projects"
          label="All Projects"
          icon={<FaCog size={20} />}
          onClick={onNavItemClick}
          requiredPermission={Permission.VIEW_ALL_PROJECTS}
        />
      </Stack>

      <Stack className={classes.footer}>
        <ProfileNavItem
          to="/profile"
          user={userWithFallback}
          isActive={location.pathname === '/profile'}
          onClick={onNavItemClick}
        />
        <NavItem label="Logout" icon={<FaSignOutAlt size={20} />} onClick={handleLogout} />
      </Stack>
    </Stack>
  );
}
